import { FC, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Icon,
  Input,
  ListItem,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Tooltip,
  UnorderedList,
} from '@chakra-ui/react';
import { isEmpty } from 'ramda';
import { BiInfoCircle } from 'react-icons/bi';

import { ValidatedFilenameErrorCode } from '../../graphql/generated';
import { pluralize } from '../../utils/pluralize/pluralize';

export type ValidationErrorMessage = {
  id: string;
  message: JSX.Element;
};

export type ValidationError = Record<
  ValidatedFilenameErrorCode,
  ValidationErrorMessage[]
>;

/**
 * A non-blocking validation warning (OCT-7346). Unlike errors, warnings never
 * prevent upload; they prompt the cedant to confirm before proceeding.
 */
export type ValidationWarningMessage = {
  id: string;
  message: JSX.Element;
};

/**
 * A file flagged by the OCT-7346 filename-pattern warning, shaped for the inline rename flow.
 * `kind: 'rename'` is a genuine naming mismatch we offer a corrected name for; `kind: 'folder'`
 * means the name is fine but the destination folder isn't expected (nothing to rename).
 */
export type FilenameWarningItem = {
  id: string;
  filepath: string;
  filename: string;
  reason: string;
  kind: 'rename' | 'folder';
  suggestedFilename: string;
};

type ErrorCodeLabelConfig = {
  action: string;
  tooltip: string;
};

type TValidatedFilenameErrorCodeLabelMap = Record<
  ValidatedFilenameErrorCode,
  ErrorCodeLabelConfig
>;

type ConfirmationProps = Props & {
  onConfirm?: () => void;
};

type Props = {
  onCancel: () => void;
  errors: ValidationError;
};

const ValidatedFilenameErrorCodeLabelMap: TValidatedFilenameErrorCodeLabelMap = {
  [ValidatedFilenameErrorCode.FILENAME_LENGTH_LIMIT]: {
    action: 'will not be uploaded',
    tooltip: 'Filenames must not contain excessive characters',
  },
  [ValidatedFilenameErrorCode.INVALID_CHARACTERS]: {
    action: 'will be renamed',
    tooltip: 'Filenames must not contain special characters',
  },
  [ValidatedFilenameErrorCode.UNSUPPORTED_FILE_TYPE]: {
    action: 'will not be uploaded',
    tooltip: 'This file type is not supported',
  },
};

export const FileValidationConfirmationModalContent: FC<ConfirmationProps> = ({
  onCancel,
  onConfirm,
  errors,
}) => {
  return (
    <ModalContent data-testid="file-validation-confirmation">
      <ModalHeader fontSize="lg" fontWeight="bold">
        Confirm changes?
      </ModalHeader>
      <ModalBody>
        <Flex direction="column" gridGap="6">
          <Text fontWeight="500">
            File validation errors were detected, please review the amendment
            list before continuing.
          </Text>
          <ValidationErrorAccordion errors={errors} />
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onConfirm} ml={3} colorScheme="green">
          Confirm Upload
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};

export const FileValidationFailingModalContent: FC<Props> = ({
  onCancel,
  errors,
}) => {
  return (
    <ModalContent data-testid="file-validation-confirmation">
      <ModalHeader fontSize="lg" fontWeight="bold">
        Upload cancelled
      </ModalHeader>
      <ModalBody>
        <Flex direction="column" gridGap="6">
          <Text fontWeight="500">
            File validation errors were detected, please review the amendment
            list and try again.
          </Text>
          <ValidationErrorAccordion errors={errors} />
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel}>Discard</Button>
      </ModalFooter>
    </ModalContent>
  );
};

type FileChoice = 'rename' | 'original';

type WarningProps = {
  onCancel: () => void;
  /** Called with a map of fileId -> chosen new file name, for the files the user opted to rename. */
  onConfirm: (renames: Record<string, string>) => void;
  items: FilenameWarningItem[];
};

/**
 * Non-blocking confirmation shown when uploaded files don't match the expected naming
 * convention (OCT-7346). For each mismatched file the cedant either accepts a suggested rename
 * (editable) or chooses to upload with the original name; "Proceed with upload" stays disabled
 * until every such file is resolved. Files whose name is fine but sit in an unexpected folder
 * are shown as an informational notice only.
 */
export const FileValidationWarningModalContent: FC<WarningProps> = ({
  onCancel,
  onConfirm,
  items,
}) => {
  const renameItems = items.filter((item) => item.kind === 'rename');
  const folderItems = items.filter((item) => item.kind === 'folder');

  const [choices, setChoices] = useState<Record<string, FileChoice>>({});
  const [names, setNames] = useState<Record<string, string>>(() =>
    Object.fromEntries(renameItems.map((item) => [item.id, item.suggestedFilename]))
  );

  const isResolved = (item: FilenameWarningItem): boolean => {
    const choice = choices[item.id];
    if (!choice) return false;
    return choice === 'original' || (names[item.id] ?? '').trim().length > 0;
  };

  const allResolved = renameItems.every(isResolved);
  const isSingle = items.length === 1;

  const handleProceed = () => {
    const renames: Record<string, string> = {};
    renameItems.forEach((item) => {
      if (choices[item.id] === 'rename') {
        renames[item.id] = (names[item.id] ?? '').trim();
      }
    });
    onConfirm(renames);
  };

  return (
    <ModalContent data-testid="file-validation-warning">
      <ModalHeader fontSize="lg" fontWeight="bold">
        Proceed with upload?
      </ModalHeader>
      <ModalBody>
        <Flex direction="column" gridGap="5">
          <Text fontWeight="500">
            {isSingle
              ? 'The file you are uploading does not match'
              : 'Some of the files you are uploading do not match'}{' '}
            the expected naming convention for this data room. Rename{' '}
            {isSingle ? 'it' : 'them'} to the suggested name, or choose to upload
            with the original name.
          </Text>

          {renameItems.map((item) => (
            <Box key={item.id}>
              <Text fontWeight="semibold" wordBreak="break-all">
                {item.filename}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {item.reason}
              </Text>
              <RadioGroup
                mt="3"
                value={choices[item.id] ?? ''}
                onChange={(value) =>
                  setChoices((prev) => ({ ...prev, [item.id]: value as FileChoice }))
                }
              >
                <Stack spacing="2">
                  <Radio value="rename" data-testid={`rename-radio-${item.id}`}>
                    Rename and upload as:
                  </Radio>
                  <Input
                    ml="6"
                    size="sm"
                    value={names[item.id] ?? ''}
                    isDisabled={choices[item.id] !== 'rename'}
                    onChange={(event) => {
                      const value = event.target.value;
                      setNames((prev) => ({ ...prev, [item.id]: value }));
                    }}
                    aria-label={`New name for ${item.filename}`}
                    data-testid={`rename-input-${item.id}`}
                  />
                  <Radio value="original" data-testid={`original-radio-${item.id}`}>
                    Upload with the original name
                  </Radio>
                </Stack>
              </RadioGroup>
            </Box>
          ))}

          {folderItems.map((item) => (
            <Box key={item.id}>
              <Text fontWeight="semibold" wordBreak="break-all">
                {item.filename}
              </Text>
              <Text fontSize="sm" color="gray.600">
                This folder isn&apos;t an expected location for this file. It can
                still be uploaded here.
              </Text>
            </Box>
          ))}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={handleProceed}
          ml={3}
          colorScheme="green"
          isDisabled={!allResolved}
          data-testid="warning-proceed"
        >
          Proceed with upload
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};

const ValidationErrorAccordion = ({ errors }: Pick<Props, 'errors'>) => {
  const errorList = Object.entries(errors);
  return (
    <Accordion allowMultiple>
      {errorList.map(([code, messages]) => {
        if (isEmpty(messages)) return null;

        const errorCode = code as ValidatedFilenameErrorCode;
        const errorLabel = ValidatedFilenameErrorCodeLabelMap[errorCode];

        return (
          <AccordionItem key={code} data-testid="validation-error-type">
            <h2>
              <AccordionButton data-testid="validation-error-show-details">
                <Flex flex="1" justifyContent="space-between">
                  <Flex gridGap="2">
                    <Text as="span" fontWeight="semibold">
                      {messages.length} {pluralize('file', messages.length)}{' '}
                      {errorLabel.action}
                    </Text>
                    <Tooltip placement="right" label={errorLabel.tooltip}>
                      <span>
                        <Icon as={BiInfoCircle} />
                      </span>
                    </Tooltip>
                  </Flex>
                  <AccordionIcon />
                </Flex>
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <UnorderedList spacing={3}>
                {messages.map(({ id, message }) => (
                  <ListItem key={id}>{message}</ListItem>
                ))}
              </UnorderedList>
            </AccordionPanel>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};
